import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';

interface AnswerSheet {
  id: string;
  UserName: string;
  UserDisplayName?: string;
  ShikakuName: string;
  ShikakuRef?: any;
  TotalScore: number;
  Cp1Score?: number;
  Cp2Score?: number;
  Cp3Score?: number;
  Cp4Score?: number;
  Cp5Score?: number;
  CreateDate?: any;
  OrderRef?: any;
  orderNumber?: string;
  [key: string]: any;
}

interface Question {
  id: string;
  Chapter: number;
  Qno: number;
  SortNo?: number;
  Question: string;
  CollectAnswer: string;
}

interface QuestionWithAnswer extends Question {
  userAnswer: string;
  isCorrect: boolean;
}


interface ShikakuOption {
  id: string;
  name: string;
}

interface BackfillResult {
  success: boolean;
  answerSheetId: string;
  examSetId: string;
  examSetVersion: number;
  updatedCount: number;
  skippedCount: number;
  errors?: Array<{ index: number; answerKey: string; selectedAnswer: string; message: string }>;
}

const PASS_SCORE = 70;
const CHAPTER_TOTAL = 20;

export default function AnswerSheetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [answerSheet, setAnswerSheet] = useState<AnswerSheet | null>(null);
  const [questions, setQuestions] = useState<QuestionWithAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect'>('all');
  const [shikakuOptions, setShikakuOptions] = useState<ShikakuOption[]>([]);
  const [selectedShikakuId, setSelectedShikakuId] = useState('');
  const [examSetVersion, setExamSetVersion] = useState('');
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState('');

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const answerSheetDoc = await getDoc(doc(db, 'AnswerSheet', id));
        if (!answerSheetDoc.exists()) {
          alert('試験結果が見つかりません');
          navigate('/admin/answers');
          return;
        }

        const answerSheetData = {
          id: answerSheetDoc.id,
          ...answerSheetDoc.data(),
        } as AnswerSheet;

        if (answerSheetData.UserRef) {
          try {
            const userDoc = await getDoc(answerSheetData.UserRef);
            if (userDoc.exists()) {
              answerSheetData.UserDisplayName =
                userDoc.data().display_name || answerSheetData.UserName || '不明';
            }
          } catch (error) {
            console.error('Error fetching user display name:', error);
          }
        }

        if (answerSheetData.OrderRef) {
          try {
            const orderDoc = await getDoc(answerSheetData.OrderRef);
            if (orderDoc.exists()) {
              const orderData = orderDoc.data() as any;
              answerSheetData.orderNumber = orderData?.orderNumber || '-';
            }
          } catch (error) {
            console.error('Error fetching order data:', error);
          }
        }

        setAnswerSheet(answerSheetData);

        const shikakuRef = answerSheetData.ShikakuRef;
        if (!shikakuRef) {
          setQuestions([]);
          return;
        }

        let sourceQuestions: any[] = [];

        if (Array.isArray(answerSheetData.QuestionSnapshot) && answerSheetData.QuestionSnapshot.length > 0) {
          sourceQuestions = answerSheetData.QuestionSnapshot;
        } else if (answerSheetData.ExamSetRef) {
          const examSetDoc = await getDoc(answerSheetData.ExamSetRef);
          if (examSetDoc.exists()) {
            const examSetData = examSetDoc.data() as any;
            sourceQuestions = Array.isArray(examSetData.questions) ? examSetData.questions : [];
          }
        }

        const questionsData: QuestionWithAnswer[] = sourceQuestions
          .map((data: any, idx: number) => {
            const questionIndex = Number(data.index || idx + 1);
            const answerKey = `ANSW${String(questionIndex).padStart(2, '0')}`;
            const userAnswer = answerSheetData[answerKey] || '';
            return {
              id: data.questionId || `${answerSheetData.id}-q-${questionIndex}`,
              ...data,
              userAnswer,
              isCorrect: userAnswer === data.CollectAnswer,
            } as QuestionWithAnswer;
          })
          .sort((a, b) => {
            if (a.Chapter !== b.Chapter) return a.Chapter - b.Chapter;
            return a.Qno - b.Qno;
          });

        setQuestions(questionsData);
      } catch (error) {
        console.error('Error fetching result detail:', error);
        alert('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id, navigate]);


  useEffect(() => {
    const fetchShikakuOptions = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'Shikaku'));
        const options = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          name: (docSnap.data() as any).ShikakuName || docSnap.id,
        }));
        setShikakuOptions(options);

        if (answerSheet?.ShikakuRef?.id) {
          setSelectedShikakuId(answerSheet.ShikakuRef.id);
        } else if (options.length > 0 && !selectedShikakuId) {
          setSelectedShikakuId(options[0].id);
        }
      } catch (error) {
        console.error('Error fetching shikaku options:', error);
      }
    };

    fetchShikakuOptions();
  }, [answerSheet?.ShikakuRef?.id, selectedShikakuId]);

  const reloadAnswerSheet = async () => {
    if (!id) return;
    const answerSheetDoc = await getDoc(doc(db, 'AnswerSheet', id));
    if (!answerSheetDoc.exists()) return;
    const answerSheetData = {
      id: answerSheetDoc.id,
      ...answerSheetDoc.data(),
    } as AnswerSheet;

    setAnswerSheet((prev) => ({ ...(prev || {}), ...answerSheetData } as AnswerSheet));

    let sourceQuestions: any[] = [];
    if (Array.isArray(answerSheetData.QuestionSnapshot) && answerSheetData.QuestionSnapshot.length > 0) {
      sourceQuestions = answerSheetData.QuestionSnapshot;
    } else if (answerSheetData.ExamSetRef) {
      const examSetDoc = await getDoc(answerSheetData.ExamSetRef);
      if (examSetDoc.exists()) {
        const examSetData = examSetDoc.data() as any;
        sourceQuestions = Array.isArray(examSetData.questions) ? examSetData.questions : [];
      }
    }

    const questionsData: QuestionWithAnswer[] = sourceQuestions
      .map((data: any, idx: number) => {
        const questionIndex = Number(data.index || idx + 1);
        const answerKey = `ANSW${String(questionIndex).padStart(2, '0')}`;
        const userAnswer = answerSheetData[answerKey] || '';
        return {
          id: data.questionId || `${answerSheetData.id}-q-${questionIndex}`,
          ...data,
          userAnswer,
          isCorrect: userAnswer === data.CollectAnswer,
        } as QuestionWithAnswer;
      })
      .sort((a, b) => {
        if (a.Chapter !== b.Chapter) return a.Chapter - b.Chapter;
        return a.Qno - b.Qno;
      });

    setQuestions(questionsData);
  };

  const handleBackfillSnapshot = async () => {
    if (!id) return;
    if (!selectedShikakuId) {
      setBackfillMessage('資格を選択してください。');
      return;
    }
    if (examSetVersion.trim() === '') {
      setBackfillMessage('バージョンを入力してください。');
      return;
    }

    const version = Number(examSetVersion);
    if (Number.isNaN(version)) {
      setBackfillMessage('バージョンは数値で入力してください。');
      return;
    }

    setIsBackfilling(true);
    setBackfillMessage('');

    try {
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      if (!projectId) {
        throw new Error('Firebase project ID is not configured.');
      }

      const response = await fetch(`https://asia-northeast1-${projectId}.cloudfunctions.net/backfillAnswerSheetSnapshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          answerSheetId: id,
          shikakuId: selectedShikakuId,
          examSetVersion: version,
        }),
      });

      const result = (await response.json()) as BackfillResult | { error?: string; detail?: string };
      if (!response.ok) {
        throw new Error((result as any).error || (result as any).detail || 'バックフィルに失敗しました。');
      }

      const successResult = result as BackfillResult;
      const errorLines = (successResult.errors || []).map((item) => `Q${item.index}: ${item.message}`);
      const message = [
        `バックフィル完了: 更新 ${successResult.updatedCount}件 / スキップ ${successResult.skippedCount}件`,
        ...errorLines,
      ].join('\n');
      setBackfillMessage(message);

      await reloadAnswerSheet();
    } catch (error) {
      console.error('Error backfilling snapshot:', error);
      setBackfillMessage(`バックフィルに失敗しました: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsBackfilling(false);
    }
  };

  const scorePercentage = answerSheet ? (answerSheet.TotalScore / 100) * 100 : 0;
  const radius = 62;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scorePercentage / 100) * circumference;

  const totalCount = questions.length;
  const correctCount = questions.filter((q) => q.isCorrect).length;
  const incorrectCount = totalCount - correctCount;

  const filteredQuestions = useMemo(() => {
    if (filter === 'correct') {
      return questions.filter((q) => q.isCorrect);
    }
    if (filter === 'incorrect') {
      return questions.filter((q) => !q.isCorrect);
    }
    return questions;
  }, [filter, questions]);

  const isPass = answerSheet ? answerSheet.TotalScore >= PASS_SCORE : false;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
  }

  if (!answerSheet) {
    return <div className="min-h-screen flex items-center justify-center">データが見つかりません</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <button
              type="button"
              onClick={() => navigate('/admin/answers')}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold"
            >
              <i className="ri-arrow-left-line"></i>
              一覧に戻る
            </button>
            <span className="text-gray-300">|</span>
            <span>管理画面</span>
            <span className="text-gray-300">/</span>
            <span>結果詳細</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm text-gray-500">
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="問題を検索..."
                className="outline-none text-sm text-gray-600 placeholder-gray-400"
              />
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
            >
              <i className="ri-file-pdf-line"></i>
              PDF書き出し
            </button>
          </div>
        </div>

        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-semibold text-amber-800 mb-3">このAnswerSheetのみスナップショットをバックフィル</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs text-amber-700 mb-1">資格</label>
              <select
                value={selectedShikakuId}
                onChange={(e) => setSelectedShikakuId(e.target.value)}
                className="w-full rounded border border-amber-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">選択してください</option>
                {shikakuOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-amber-700 mb-1">ExamSet バージョン</label>
              <input
                type="number"
                value={examSetVersion}
                onChange={(e) => setExamSetVersion(e.target.value)}
                className="w-full rounded border border-amber-300 bg-white px-3 py-2 text-sm"
                placeholder="例: 0"
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={handleBackfillSnapshot}
                disabled={isBackfilling}
                className="inline-flex items-center gap-2 rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                <i className="ri-refresh-line"></i>
                {isBackfilling ? 'バックフィル中...' : 'このAnswerSheetをバックフィル'}
              </button>
            </div>
          </div>
          {backfillMessage && (
            <pre className="mt-3 whitespace-pre-wrap rounded bg-white p-3 text-xs text-amber-900 border border-amber-200">{backfillMessage}</pre>
          )}
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">試験結果詳細</h1>

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
          <div className="p-8 space-y-4">
            <div className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold">
              <i className="ri-checkbox-circle-line"></i>
              試験完了
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">
                {answerSheet.UserDisplayName || answerSheet.UserName}
                <span className="text-lg text-gray-500 ml-2">({answerSheet.UserName})</span>
              </div>
              <div className="text-sm text-gray-500 mt-2">注文番号: {answerSheet.orderNumber || '-'}</div>
            </div>
            <div className="text-base text-gray-700">{answerSheet.ShikakuName}</div>

            <div className="border-t border-gray-100 pt-4 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-gray-400">試験日時</div>
                <div className="text-gray-700 font-semibold mt-1">
                  {answerSheet.CreateDate?.toDate
                    ? answerSheet.CreateDate.toDate().toLocaleString('ja-JP')
                    : '不明'}
                </div>
              </div>
              <div>
                <div className="text-gray-400">受験回数</div>
                <div className="text-gray-700 font-semibold mt-1">
                  {answerSheet.ExamCount ?? '-'}
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-100">
            <div className="flex items-center gap-6">
              <div className="relative w-40 h-40">
                <svg className="transform -rotate-90 w-40 h-40">
                  <circle
                    cx="80"
                    cy="80"
                    r={radius}
                    stroke="#e5e7eb"
                    strokeWidth="10"
                    fill="none"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r={radius}
                    stroke="#1e40af"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-3xl font-bold text-gray-900">{answerSheet.TotalScore}</div>
                  <div className="text-gray-500 text-xs">/ 100</div>
                </div>
              </div>

              <div className="space-y-3 flex-1">
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center rounded-full px-4 py-2 text-xs font-semibold ${
                      isPass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {isPass ? '合格' : '不合格'}
                  </span>
                  <span className="text-xs text-gray-400">合格基準: {PASS_SCORE}点</span>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-500 mb-3">章別得点</div>
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((chapter) => {
                      const score = answerSheet[`Cp${chapter}Score`] || 0;
                      const percentage = Math.min((score / CHAPTER_TOTAL) * 100, 100);
                      return (
                        <div key={chapter} className="flex items-center gap-4">
                          <div className="w-10 text-xs text-gray-600">{chapter}章</div>
                          <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className="h-full bg-blue-600"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="w-14 text-right text-xs font-semibold text-gray-700">
                            {score}/{CHAPTER_TOTAL}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">問題別詳細</h2>
            <div className="inline-flex bg-gray-100 rounded-full p-1 text-xs font-semibold text-gray-600">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-full ${
                  filter === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'
                }`}
              >
                すべて ({totalCount})
              </button>
              <button
                type="button"
                onClick={() => setFilter('correct')}
                className={`px-4 py-2 rounded-full ${
                  filter === 'correct' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'
                }`}
              >
                正解 ({correctCount})
              </button>
              <button
                type="button"
                onClick={() => setFilter('incorrect')}
                className={`px-4 py-2 rounded-full ${
                  filter === 'incorrect' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'
                }`}
              >
                不正解 ({incorrectCount})
              </button>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-6 py-3 text-left">#</th>
                  <th className="px-6 py-3 text-left">章</th>
                  <th className="px-6 py-3 text-left">問題内容</th>
                  <th className="px-6 py-3 text-left">回答</th>
                  <th className="px-6 py-3 text-left">正解</th>
                  <th className="px-6 py-3 text-right">結果</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredQuestions.map((question, index) => (
                  <tr key={question.id}>
                    <td className="px-6 py-4 text-gray-400">
                      {String(index + 1).padStart(2, '0')}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
                        {question.Chapter}章
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-800 max-w-md">
                      {question.Question}
                    </td>
                    <td className="px-6 py-4 text-gray-700">{question.userAnswer || '-'}</td>
                    <td className="px-6 py-4 text-gray-700">{question.CollectAnswer}</td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                          question.isCorrect
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        <i
                          className={question.isCorrect ? 'ri-check-line' : 'ri-close-line'}
                        ></i>
                        {question.isCorrect ? '正解' : '不正解'}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredQuestions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                      表示する問題がありません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
